import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import firebase from "../../firebase";
import ToolbarWithLogo from "../ToolbarWithLogo";
import { primarySurface, primaryTintBorder } from "../../surfaceTints";
import DesktopAppPageFrame from "./DesktopAppPageFrame";
import { rememberDesktopLicence } from "./useCurrentDesktopLicence";
import {
    GRACE_DAYS,
    formatLicenceDate,
    licenceIsCurrent,
    macsLabel,
} from "./desktopAccess";

// Where someone given Nodiant Coder finds their key again. The key email and
// Desktop App in the menu link here; it only ever shows the signed-in user's
// own licence.
export default function MyDesktopAppPage() {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        let cancelled = false;

        const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
                return;
            }

            try {
                const result = await firebase
                    .functions()
                    .httpsCallable("getMyDesktopLicence")();
                // Keeps the menu's Desktop App shortcut in step with this page
                rememberDesktopLicence(user.uid, result.data && result.data.licence);
                if (!cancelled) setState({ loading: false, ...result.data });
            } catch (error) {
                if (!cancelled) {
                    setState({
                        loading: false,
                        error:
                            error.message ||
                            "Your desktop app licence couldn't be loaded",
                    });
                }
            }
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [history, location.pathname]);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(state.licence.licenseKey);
            enqueueSnackbar("Activation key copied", { variant: "success" });
        } catch (error) {
            enqueueSnackbar("Couldn't copy — select the key and copy it instead", {
                variant: "warning",
            });
        }
    }

    function renderBody() {
        if (state.loading) {
            return (
                <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
                    <CircularProgress size={32} aria-label="Loading" />
                </Box>
            );
        }

        if (state.error) {
            return (
                <Typography color="error" sx={{ p: 3 }}>
                    {state.error}
                </Typography>
            );
        }

        const licence = state.licence;

        if (!licence) {
            return (
                <Typography sx={{ p: 3, color: "text.secondary" }}>
                    You don't have access to Nodiant Coder yet. Your System
                    Digital Manager can give it to you.
                </Typography>
            );
        }

        const current = licenceIsCurrent(licence);

        return (
            <Box sx={{ p: { xs: 2.5, sm: 3.5 }, display: "grid", gap: 3 }}>
                <Box>
                    <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", letterSpacing: "0.06em" }}
                    >
                        Your activation key
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: primaryTintBorder,
                            backgroundColor: primarySurface,
                        }}
                    >
                        <Typography
                            component="code"
                            sx={{
                                flexGrow: 1,
                                fontFamily: "Menlo, Consolas, monospace",
                                fontSize: { xs: 18, sm: 24 },
                                letterSpacing: "0.08em",
                                userSelect: "all",
                                wordBreak: "break-all",
                            }}
                        >
                            {licence.licenseKey}
                        </Typography>
                        <Tooltip title="Copy key">
                            <IconButton aria-label="Copy activation key" onClick={handleCopy}>
                                <ContentCopyOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1,
                            color: current ? "text.secondary" : "warning.main",
                        }}
                    >
                        {current
                            ? "Valid until " + formatLicenceDate(licence.expiryDate)
                            : "Expired " +
                              formatLicenceDate(licence.expiryDate) +
                              ". Ask your System Digital Manager for a new key."}
                        {current &&
                            " · Works on " +
                                macsLabel(licence.maxDevices) +
                                ((licence.activationCount || 0) > 0
                                    ? " (" + licence.activationCount + " in use)"
                                    : "")}
                    </Typography>
                </Box>

                <Box>
                    <Button
                        variant="contained"
                        disableElevation
                        component="a"
                        href={state.downloadUrl}
                        startIcon={<DownloadOutlinedIcon />}
                        sx={{ fontWeight: 700, px: 2.5, py: 1.1 }}
                    >
                        Download Nodiant Coder
                    </Button>
                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                        For Mac.
                    </Typography>
                </Box>

                <Box component="ol" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                        Download the installer and open it to install Nodiant
                        Coder.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                        Open Nodiant Coder and enter the key above when asked.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                        The key works on {macsLabel(licence.maxDevices)}. To move
                        it to a different Mac, choose Nodiant Coder › Release This
                        Mac… on the old one first.
                    </Typography>
                    <Typography component="li" variant="body2">
                        After it expires the app keeps working for {GRACE_DAYS} days while
                        you get a new key. To use a new key, choose Nodiant Coder ›
                        Enter New License….
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <DesktopAppPageFrame
                title="Nodiant Coder"
                subtitle="The desktop app for coding and analysing video."
            >
                {renderBody()}
            </DesktopAppPageFrame>
            <ToolbarWithLogo />
        </>
    );
}
